import React from "react";
import { motion } from "framer-motion";

interface OverlayBannerProps {
  children: React.ReactNode;
  message?: string;
  className?: string;
}

const OverlayBanner: React.FC<OverlayBannerProps> = ({
  children,
  message = "Coming Soon",
  className = "",
}) => {
  return (
    <div
      className={`relative w-full h-full overflow-hidden rounded-2xl z-0 ${className}`}
    >
      {/* Ensure child content never exceeds our z-layer */}
      <div className="w-full h-full relative z-0 filter grayscale">
        {children}
      </div>

      {/* Overlay always on top */}
      <div className="absolute inset-0 z-[9999] pointer-events-none">
        {/* Optional semi-transparent dim overlay */}
        <div className="absolute inset-0 bg-white/5 backdrop-blur-[0px]" />

        {/* Animated ribbon */}
        <div className="absolute bottom-0 left-0 w-full h-8 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 text-white font-semibold text-lg flex items-center justify-center shadow-2xl border border-white/20 backdrop-blur-md overflow-hidden">
          {/* Shine animation */}
          <motion.div
            className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-white/70 to-transparent opacity-70 blur-[3px]"
            animate={{ x: ["-150%", "250%"] }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut" as const,
            }}
          />
          <span className="relative z-10 px-6 tracking-wide drop-shadow-lg text-sm">
            {message}
          </span>
        </div>
      </div>

      {/* Optional soft reflection at bottom */}
      <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-white/10 to-transparent z-[500] pointer-events-none" />
    </div>
  );
};

export default OverlayBanner;

interface OverlayBadgeProps {
  children: React.ReactNode;
  label?: string;
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  className?: string;
  size?: "sm" | "md";
}

export const OverlayBadge: React.FC<OverlayBadgeProps> = ({
  children,
  label = "Coming Soon",
  position = "top-right",
  className = "",
  size = "md",
}) => {
  const posMap: Record<string, string> = {
    "top-right": "top-0 right-12 translate-x-1/2 -translate-y-1/2",
    "top-left": "top-0 left-0 -translate-x-1/2 -translate-y-1/2",
    "bottom-right": "bottom-0 right-0 translate-x-1/2 translate-y-1/2",
    "bottom-left": "bottom-0 left-0 -translate-x-1/2 translate-y-1/2",
  };

  const badgeSize =
    size === "sm" ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1";

  return (
    <div className={`relative inline-block ${className}`}>
      {children}

      <div className={`absolute ${posMap[position]} z-20 pointer-events-none`}>
        <div
          className={`relative overflow-hidden rounded-md bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 text-white font-semibold shadow-lg backdrop-blur-md border border-white/20 ${badgeSize}`}
        >
          <motion.div
            className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-white/70 to-transparent opacity-70 blur-[2px]"
            animate={{ x: ["-150%", "300%"] }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut" as const,
            }}
          />
          <span className="relative z-10 whitespace-nowrap">{label}</span>
        </div>
      </div>
    </div>
  );
};
